import { CurrencyPipe, DatePipe, DecimalPipe, PercentPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgLoggerModule } from '@nsalaun/ng-logger';
import * as Sentry from '@sentry/angular';
import { OAuthModule } from 'angular-oauth2-oidc';

import { BoolPipe } from '@mysvg/utils/pipes/bool.pipe';
import { CentsToEuroPipe } from '@mysvg/utils/pipes/cents-to-euro.pipe';
import { EnumPipe } from '@mysvg/utils/pipes/enum.pipe';
import { ListMappedPropertyToStringPipe } from '@mysvg/utils/pipes/list-property/list-mapped-property-to-string.pipe';
import { StatePipe } from '@mysvg/utils/pipes/state.pipe';
import { DialogModule, MAC_MAT_DIALOG_CONFIG } from '@svg-frontends/dialog/dialog.module';
import { VehicleDocumentsPipe } from '@svg-frontends/document/pipes/vehicle-documents.pipe';
import { GlobalErrorHandlerService } from '@svg-frontends/error/services/global-error-handler.service';
import { AuthenticationInterceptorService } from '@svg-frontends/session/authentication/interceptors/auth-interceptor.service';
import { ErrorResponseInterceptorService } from '@svg-frontends/session/authentication/interceptors/error-interceptor/error-response-interceptor.service';
import { AuthenticationService } from '@svg-frontends/session/authentication/services/authentication.service';
import { DISCOVERY_DOCUMENT } from '@svg-frontends/session/authentication/services/oauth-wrapper.service';
import { SvgAuthenticationService } from '@svg-frontends/session/authentication/services/svg-authentication.service';
import { SvgCustomerGroupIdHeaderInterceptor } from '@svg-frontends/session/context/interceptors/svg-customer-group-id-header-interceptor';
import { ColumnPipe } from '@svg-frontends/table-view/core/pipes/column.pipe';

import { API_BASE_URLS_TOKEN, APPLICATION_NAME, APP_VERSION_TOKEN, STAGE_TOKEN } from '@svg/environment/injection-tokens';
import { StagesEnum } from '@svg/environment/stages.enum';

import packageJson from '../../../../package.json';
import { API_BASE_URLS } from '../environments/api-base-urls';
import { ENVIRONMENT } from '../environments/environment';
import { ApiModule } from './api.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorMessageModule } from './error-message.module';
import { RouterDebugService } from './router-debug.service';

// the second parameter 'de' is optional
registerLocaleData(localeDe, 'de');

if (ENVIRONMENT.sentry.url !== null) {
	const sentryConfig: Sentry.BrowserOptions = {
		dsn: ENVIRONMENT.sentry.url,
		environment: ENVIRONMENT.name,
		release: packageJson.version,
		integrations: [
			Sentry.browserTracingIntegration({
				// [CAUTION] this option results in cors errors
				// tracingOrigins: ['localhost', 'staging.myasi.de', 'api.staging.myasi.de', 'myasi.de', 'api.myasi.de'],
			}),
			Sentry.replayIntegration(),
		],
		replaysSessionSampleRate: ENVIRONMENT.sentry.replaysSessionSampleRate,
		replaysOnErrorSampleRate: ENVIRONMENT.sentry.replaysOnErrorSampleRate,
		// Only track errors from our domains (ignore browser errors etc.)
		allowUrls: [/https?:\/\/.*\.?mysvg\.de/],
		denyUrls: [
			// Do not track Chrome extension errors
			/extensions\//i,
			/^chrome:\/\//i,
			/^chrome-extension:\/\//i,
		],
	};

	// important to also execute on staging so potential erros get caught before prod deploy
	if ([StagesEnum.PRODUCTION, StagesEnum.STAGING].includes(ENVIRONMENT.name)) {
		// on prod and staging do dynamic sampling depending on pages
		sentryConfig.tracesSampler = ({ name, inheritOrSampleWith }) => {
			switch (name) {
				case '/':
					// send less transaction for busy pages (half of half so 0.25)
					return ENVIRONMENT.sentry.tracesSampleRate / 2;
				case '/dashboard':
					return ENVIRONMENT.sentry.tracesSampleRate / 2;
				case '/staff/dashboard':
					return ENVIRONMENT.sentry.tracesSampleRate / 2;
				default:
					// default traces sample rate for prod is half (0.5) for pages not filtered more precisely
					return inheritOrSampleWith(ENVIRONMENT.sentry.tracesSampleRate);
			}
		};
	} else {
		// all other stages use sample rate defined in environment
		sentryConfig.tracesSampleRate = ENVIRONMENT.sentry.tracesSampleRate;
	}

	Sentry.init(sentryConfig);
}

@NgModule({
	bootstrap: [AppComponent],
	declarations: [AppComponent],
	imports: [
		ApiModule,
		OAuthModule.forRoot(),
		BrowserAnimationsModule,
		BrowserModule,
		DialogModule,
		ErrorMessageModule,
		NgLoggerModule.forRoot(ENVIRONMENT.logLevel),
		AppRoutingModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: ENVIRONMENT.enabledUpdateServiceWorker,
			registrationStrategy: 'registerWhenStable:30000',
		}),
	],
	providers: [
		{ provide: RouterDebugService },
		{
			provide: APP_INITIALIZER,
			useFactory: (svc: RouterDebugService) => () => svc.init(),
			deps: [RouterDebugService],
			multi: true,
		},
		// General Injection Tokens
		{ provide: LOCALE_ID, useValue: 'de' },
		// Version from single source of truth package.json
		{ provide: APP_VERSION_TOKEN, useValue: packageJson.version },
		// ENVIRONMENT Injection Tokens
		{ provide: APPLICATION_NAME, useValue: ENVIRONMENT.applicationName },
		{ provide: STAGE_TOKEN, useValue: ENVIRONMENT.name },
		{ provide: API_BASE_URLS_TOKEN, useValue: API_BASE_URLS },
		// AUTH Injection Tokens
		{ provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptorService, multi: true },
		{ provide: AuthenticationService, useExisting: SvgAuthenticationService },
		{
			provide: DISCOVERY_DOCUMENT,
			useValue: {
				clientId: ENVIRONMENT.clientId,
				issuer: ENVIRONMENT.tokenIssuer,
				scope: ENVIRONMENT.scope,
			},
		},
		{ provide: ErrorHandler, useClass: GlobalErrorHandlerService },
		// Provide group id (portal uuid) to backend in custom header
		// Active while in customer context or staff@customer mode
		{ provide: HTTP_INTERCEPTORS, useClass: SvgCustomerGroupIdHeaderInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ErrorResponseInterceptorService, multi: true },
		{ provide: LOCALE_ID, useValue: 'de' },
		{ provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: MAC_MAT_DIALOG_CONFIG },
		// Sentry Injection Tokens
		{ provide: Sentry.TraceService, deps: [Router] },
		provideAppInitializer(() => {
			inject(Sentry.TraceService);
		}),
		// OTHER Injection Tokens
		{ provide: ErrorHandler, useClass: GlobalErrorHandlerService },
		{ provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: MAC_MAT_DIALOG_CONFIG },
		BoolPipe,
		ColumnPipe,
		CurrencyPipe,
		DatePipe,
		DecimalPipe,
		EnumPipe,
		CentsToEuroPipe,
		ListMappedPropertyToStringPipe,
		PercentPipe,
		StatePipe,
		VehicleDocumentsPipe,
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class AppModule {
	// initialize sentry tracing

	constructor(_trace: Sentry.TraceService) {
		console.log('[AppModule] says found window.location.search:' + window.location.search);
	}
}
